/**
 * The signed-in staff member's `users` profile (name + role). The row is
 * readable by its owner under RLS; the JWT `role` claim stays the auth
 * decision, this is for display only.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface CurrentProfile {
  id: string;
  full_name: string | null;
  email: string;
  role: string;
}

export function useCurrentProfile() {
  const { user } = useAuth();
  const authUserId = user?.id ?? null;
  return useQuery({
    queryKey: ['current-profile', authUserId],
    enabled: Boolean(authUserId),
    queryFn: async (): Promise<CurrentProfile | null> => {
      if (!authUserId) return null;
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name, email, role')
        .eq('auth_user_id', authUserId)
        .maybeSingle();
      if (error) throw error;
      return (data as CurrentProfile | null) ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function humanizeRole(role: string | null | undefined): string {
  if (!role) return '';
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function initialsFromName(name: string | null | undefined, fallback: string): string {
  const trimmed = (name ?? '').trim();
  if (!trimmed) return fallback;
  const letters = trimmed
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return letters || fallback;
}
